import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api/rest';
import type { KickReason, LinkResponse, MeResponse } from './api/protocol';
import { Brand } from './components/Brand';
import { BRAND } from './components/Logo';
import { Rail, type RailTarget } from './components/Rail';
import { StoreLink } from './components/StoreLink';
import { usePrefs, useVoice } from './hooks';
import { LinkPage } from './pages/LinkPage';
import { SettingsPage } from './pages/SettingsPage';
import { VoicePage } from './pages/VoicePage';
import { parseRoute, useLocationKey, navigate } from './router';
import { createSession, type VoiceSession } from './session';
import type { VoiceController } from './voice/VoiceController';

type Auth = { kind: 'loading' } | { kind: 'anon'; notice: string | null } | { kind: 'error' } | { kind: 'linked'; me: MeResponse };

const KICK_NOTICE: Record<KickReason, string> = {
  OTHER_TAB: '',
  UNLINKED: 'Questo browser è stato scollegato (/voice unlink). Ricollegati per usare la voce.',
  SESSION_EXPIRED: 'La sessione è scaduta. Ricollegati con /voice.',
};

/** Push-to-talk keyboard handling (only works while the tab has focus: browser limit, D14). */
function usePushToTalk(controller: VoiceController | null) {
  const prefs = usePrefs();
  useEffect(() => {
    if (!controller || prefs.micMode !== 'ptt') return;
    const isTyping = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
    const down = (e: KeyboardEvent) => {
      if (e.code !== prefs.pttKey || e.repeat || isTyping(e.target) || document.body.dataset.capturingKey) return;
      e.preventDefault();
      controller.setPushToTalk(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === prefs.pttKey) controller.setPushToTalk(false);
    };
    const release = () => controller.setPushToTalk(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', release);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', release);
      controller.setPushToTalk(false);
    };
  }, [controller, prefs.micMode, prefs.pttKey]);
}

function OtherTab({ onTakeOver }: { onTakeOver: () => void }) {
  return (
    <main className="center-page" data-testid="other-tab">
      <section className="card narrow-card">
        <h1 className="display display-sm">Aperta in un’altra scheda</h1>
        <p className="muted">La voice chat può essere attiva in una sola scheda alla volta. Hai aperto Titanet Voice altrove.</p>
        <button type="button" className="btn btn-primary" onClick={onTakeOver} data-testid="take-over">
          Usa questa scheda
        </button>
      </section>
    </main>
  );
}

export function App() {
  const locKey = useLocationKey();
  const route = useMemo(() => parseRoute(window.location.pathname, window.location.hash), [locKey]);
  const [auth, setAuth] = useState<Auth>({ kind: 'loading' });
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [otherTab, setOtherTab] = useState(false);
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    api.config().then((c) => setVersion(c.version), () => setVersion(null));
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const me = await api.me();
      setAuth({ kind: 'linked', me });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setAuth((a) => ({ kind: 'anon', notice: a.kind === 'anon' ? a.notice : null }));
      else setAuth({ kind: 'error' });
    }
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  // One voice session while linked (survives navigation between Voce and Impostazioni).
  const linked = auth.kind === 'linked' ? auth.me.uuid : null;
  useEffect(() => {
    if (!linked) return;
    const s = createSession({
      onKick: (reason) => {
        if (reason === 'OTHER_TAB') setOtherTab(true);
        else setAuth({ kind: 'anon', notice: KICK_NOTICE[reason] });
      },
      onUnauthorized: () => setAuth({ kind: 'anon', notice: KICK_NOTICE.SESSION_EXPIRED }),
      onMaybeUnauthorized: () => {
        api.me().catch((e) => {
          if (e instanceof ApiError && e.status === 401) {
            setAuth({ kind: 'anon', notice: KICK_NOTICE.SESSION_EXPIRED });
          }
        });
      },
    });
    setSession(s);
    return () => {
      s.dispose();
      setSession(null);
    };
  }, [linked]);

  usePushToTalk(session?.controller ?? null);

  const onLinked = (_r: LinkResponse) => {
    setOtherTab(false);
    setAuth({ kind: 'loading' });
    navigate('/', { replace: true });
    void loadMe();
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* the cookie may already be invalid */
    }
    setAuth({ kind: 'anon', notice: null });
    navigate('/', { replace: true });
  };

  const me = auth.kind === 'linked' ? auth.me : null;

  let content: React.ReactNode;
  if (route.name === 'link' && route.code) {
    content = <LinkPage initialCode={route.code} onLinked={onLinked} />;
  } else if (auth.kind === 'loading') {
    content = (
      <main className="center-page" aria-busy="true">
        <span className="loader" aria-label="Caricamento" />
      </main>
    );
  } else if (auth.kind === 'error') {
    content = (
      <main className="center-page">
        <section className="card narrow-card" role="alert">
          <h1 className="display display-sm">Server non raggiungibile</h1>
          <p className="muted">Non riesco a contattare Titanet Voice. Controlla la connessione e riprova.</p>
          <button type="button" className="btn btn-primary" onClick={() => void loadMe()}>
            Riprova
          </button>
        </section>
      </main>
    );
  } else if (auth.kind === 'anon' || route.name === 'link') {
    content = <LinkPage notice={auth.kind === 'anon' ? auth.notice : null} onLinked={onLinked} />;
  } else if (otherTab && session) {
    content = (
      <OtherTab
        onTakeOver={() => {
          setOtherTab(false);
          session.socket.start();
        }}
      />
    );
  } else if (!session) {
    content = (
      <main className="center-page" aria-busy="true">
        <span className="loader" aria-label="Caricamento" />
      </main>
    );
  } else if (route.name === 'settings') {
    content = <SettingsPage controller={session.controller} me={me} version={version} onLogout={logout} />;
  } else {
    content = <VoicePage controller={session.controller} />;
  }

  const railActive: RailTarget = route.name === 'settings' ? 'settings' : 'voice';
  const showRail = !!me && !!session && route.name !== 'link';

  return (
    <div className={`app${showRail ? ' has-rail' : ''}`}>
      <div className="backdrop" aria-hidden />
      {showRail ? (
        <RailLive controller={session!.controller} me={me!} active={railActive} />
      ) : (
        <header className="topbar">
          <Brand compact />
          <StoreLink />
        </header>
      )}
      <div className="app-content">
        {showRail ? (
          <header className="app-brandbar">
            <Brand compact />
            <StoreLink />
          </header>
        ) : null}
        {content}
      </div>
      <span className="version-tag" data-testid="version">
        {BRAND.product} · {BRAND.domain}
        {version ? ` · v${version}` : ''}
      </span>
    </div>
  );
}

function RailLive({ controller, me, active }: { controller: VoiceController; me: MeResponse; active: RailTarget }) {
  const s = useVoice(controller);
  return <Rail me={me} active={active} live={s.entered && s.phase === 'connected'} />;
}
